import { type V2, clamp } from '../core/math';

/**
 * Walkability grid over a zone plus A* pathfinding. Obstacles are rasterised
 * already inflated by the standard actor radius, so a path through free cells
 * is valid for a normal-sized actor treated as a point.
 */
export class NavGrid {
  readonly cols: number;
  readonly rows: number;
  /** 0 = walkable, 1 = blocked. */
  readonly blocked: Uint8Array;

  constructor(
    readonly minX: number,
    readonly minZ: number,
    readonly width: number,
    readonly depth: number,
    readonly cell = 0.5,
  ) {
    this.cols = Math.ceil(width / cell);
    this.rows = Math.ceil(depth / cell);
    this.blocked = new Uint8Array(this.cols * this.rows);
  }

  cellOf(p: V2): [number, number] {
    return [Math.floor((p.x - this.minX) / this.cell), Math.floor((p.z - this.minZ) / this.cell)];
  }

  centerOf(cx: number, cz: number): V2 {
    return { x: this.minX + (cx + 0.5) * this.cell, z: this.minZ + (cz + 0.5) * this.cell };
  }

  inBounds(cx: number, cz: number): boolean {
    return cx >= 0 && cz >= 0 && cx < this.cols && cz < this.rows;
  }

  isBlockedCell(cx: number, cz: number): boolean {
    return !this.inBounds(cx, cz) || this.blocked[cz * this.cols + cx] === 1;
  }

  walkable(p: V2): boolean {
    const [cx, cz] = this.cellOf(p);
    return !this.isBlockedCell(cx, cz);
  }

  setCell(cx: number, cz: number, blocked: boolean): void {
    if (this.inBounds(cx, cz)) this.blocked[cz * this.cols + cx] = blocked ? 1 : 0;
  }

  /** Rasterises a predicate over the bounding box [x0,x1]x[z0,z1]. */
  private fill(x0: number, z0: number, x1: number, z1: number, test: (p: V2) => boolean, blocked: boolean): void {
    const [c0, r0] = this.cellOf({ x: x0, z: z0 });
    const [c1, r1] = this.cellOf({ x: x1, z: z1 });
    for (let cz = Math.max(0, r0); cz <= Math.min(this.rows - 1, r1); cz++) {
      for (let cx = Math.max(0, c0); cx <= Math.min(this.cols - 1, c1); cx++) {
        if (test(this.centerOf(cx, cz))) this.blocked[cz * this.cols + cx] = blocked ? 1 : 0;
      }
    }
  }

  blockCircle(c: V2, r: number, blocked = true): void {
    const r2 = r * r;
    this.fill(c.x - r, c.z - r, c.x + r, c.z + r, (p) => (p.x - c.x) ** 2 + (p.z - c.z) ** 2 <= r2, blocked);
  }

  /** Oriented rectangle centred at `c` with half extents and rotation (radians, XZ plane). */
  blockRect(c: V2, halfW: number, halfD: number, rot = 0, blocked = true): void {
    const cos = Math.cos(-rot);
    const sin = Math.sin(-rot);
    const reach = Math.hypot(halfW, halfD);
    this.fill(
      c.x - reach,
      c.z - reach,
      c.x + reach,
      c.z + reach,
      (p) => {
        const dx = p.x - c.x;
        const dz = p.z - c.z;
        const lx = dx * cos + dz * sin;
        const lz = -dx * sin + dz * cos;
        return Math.abs(lx) <= halfW && Math.abs(lz) <= halfD;
      },
      blocked,
    );
  }

  /** Point-in-polygon rasterisation (even-odd rule). */
  blockPolygon(pts: readonly V2[], blocked = true): void {
    if (pts.length < 3) return;
    let x0 = Infinity;
    let z0 = Infinity;
    let x1 = -Infinity;
    let z1 = -Infinity;
    for (const p of pts) {
      x0 = Math.min(x0, p.x);
      z0 = Math.min(z0, p.z);
      x1 = Math.max(x1, p.x);
      z1 = Math.max(z1, p.z);
    }
    this.fill(x0, z0, x1, z1, (p) => pointInPolygon(p, pts), blocked);
  }

  /** Blocks everything outside a circle (arena walls). */
  blockOutsideCircle(c: V2, r: number): void {
    const r2 = r * r;
    for (let cz = 0; cz < this.rows; cz++) {
      for (let cx = 0; cx < this.cols; cx++) {
        const p = this.centerOf(cx, cz);
        if ((p.x - c.x) ** 2 + (p.z - c.z) ** 2 > r2) this.blocked[cz * this.cols + cx] = 1;
      }
    }
  }

  /** Grid-walk line of sight (samples at quarter-cell resolution). */
  lineOfSight(a: V2, b: V2): boolean {
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const d = Math.hypot(dx, dz);
    const steps = Math.max(1, Math.ceil(d / (this.cell * 0.25)));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      if (!this.walkable({ x: a.x + dx * t, z: a.z + dz * t })) return false;
    }
    return true;
  }

  /** Nearest walkable cell centre to `p` (spiral search), or null if none within `maxRadius` cells. */
  nearestWalkable(p: V2, maxRadius = 40): V2 | null {
    if (this.walkable(p)) return { ...p };
    const [cx, cz] = this.cellOf(p);
    for (let r = 1; r <= maxRadius; r++) {
      let best: V2 | null = null;
      let bestD = Infinity;
      for (let ox = -r; ox <= r; ox++) {
        for (let oz = -r; oz <= r; oz++) {
          if (Math.max(Math.abs(ox), Math.abs(oz)) !== r) continue;
          if (this.isBlockedCell(cx + ox, cz + oz)) continue;
          const c = this.centerOf(cx + ox, cz + oz);
          const d = (c.x - p.x) ** 2 + (c.z - p.z) ** 2;
          if (d < bestD) {
            bestD = d;
            best = c;
          }
        }
      }
      if (best) return best;
    }
    return null;
  }

  /**
   * Moves from `pos` by `delta`, sliding along blocked cells instead of
   * stopping dead (so walls feel smooth when running diagonally into them).
   */
  slide(pos: V2, delta: V2): V2 {
    const full = { x: pos.x + delta.x, z: pos.z + delta.z };
    if (this.walkable(full)) return full;
    const onlyX = { x: pos.x + delta.x, z: pos.z };
    if (delta.x !== 0 && this.walkable(onlyX)) return onlyX;
    const onlyZ = { x: pos.x, z: pos.z + delta.z };
    if (delta.z !== 0 && this.walkable(onlyZ)) return onlyZ;
    return { ...pos };
  }

  /**
   * A* over 8-connected cells (no corner cutting), followed by string-pulling
   * so the result is a short list of straight-line waypoints. Returns null if
   * unreachable within the iteration budget.
   */
  findPath(from: V2, to: V2, maxIterations = 20000): V2[] | null {
    const start = this.nearestWalkable(from, 4);
    const goal = this.nearestWalkable(to, 12);
    if (!start || !goal) return null;
    if (this.lineOfSight(start, goal)) return [goal];

    const [sx, sz] = this.cellOf(start);
    const [gx, gz] = this.cellOf(goal);
    const cols = this.cols;
    const startIdx = sz * cols + sx;
    const goalIdx = gz * cols + gx;
    const g = new Float32Array(this.cols * this.rows).fill(Infinity);
    const came = new Int32Array(this.cols * this.rows).fill(-1);
    const closed = new Uint8Array(this.cols * this.rows);
    const open = new MinHeap();
    const h = (idx: number) => {
      const x = idx % cols;
      const z = (idx / cols) | 0;
      const ddx = Math.abs(x - gx);
      const ddz = Math.abs(z - gz);
      return ddx + ddz + (Math.SQRT2 - 2) * Math.min(ddx, ddz);
    };
    g[startIdx] = 0;
    open.push(startIdx, h(startIdx));

    let iterations = 0;
    let found = false;
    while (open.size > 0 && iterations++ < maxIterations) {
      const cur = open.pop();
      if (cur === goalIdx) {
        found = true;
        break;
      }
      if (closed[cur]) continue;
      closed[cur] = 1;
      const cx = cur % cols;
      const cz = (cur / cols) | 0;
      for (let ox = -1; ox <= 1; ox++) {
        for (let oz = -1; oz <= 1; oz++) {
          if (ox === 0 && oz === 0) continue;
          const nx = cx + ox;
          const nz = cz + oz;
          if (this.isBlockedCell(nx, nz)) continue;
          // No corner cutting through blocked diagonals.
          if (ox !== 0 && oz !== 0 && (this.isBlockedCell(cx + ox, cz) || this.isBlockedCell(cx, cz + oz))) continue;
          const ni = nz * cols + nx;
          if (closed[ni]) continue;
          const ng = (g[cur] as number) + (ox !== 0 && oz !== 0 ? Math.SQRT2 : 1);
          if (ng < (g[ni] as number)) {
            g[ni] = ng;
            came[ni] = cur;
            open.push(ni, ng + h(ni));
          }
        }
      }
    }
    if (!found) return null;

    const cells: V2[] = [];
    for (let i = goalIdx; i !== -1 && i !== startIdx; i = came[i] as number) {
      cells.push(this.centerOf(i % cols, (i / cols) | 0));
    }
    cells.reverse();
    if (cells.length > 0) cells[cells.length - 1] = goal;
    return this.smooth(start, cells);
  }

  private smooth(start: V2, pts: V2[]): V2[] {
    const out: V2[] = [];
    let anchor = start;
    let i = 0;
    while (i < pts.length) {
      let j = pts.length - 1;
      while (j > i && !this.lineOfSight(anchor, pts[j] as V2)) j--;
      const p = pts[j] as V2;
      out.push(p);
      anchor = p;
      i = j + 1;
    }
    return out;
  }

  /** Clamp a point into the grid bounds. */
  clampToBounds(p: V2): V2 {
    return {
      x: clamp(p.x, this.minX + this.cell * 0.5, this.minX + this.width - this.cell * 0.5),
      z: clamp(p.z, this.minZ + this.cell * 0.5, this.minZ + this.depth - this.cell * 0.5),
    };
  }
}

export function pointInPolygon(p: V2, pts: readonly V2[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i] as V2;
    const b = pts[j] as V2;
    if (a.z > p.z !== b.z > p.z && p.x < ((b.x - a.x) * (p.z - a.z)) / (b.z - a.z) + a.x) inside = !inside;
  }
  return inside;
}

/** Binary min-heap of (index, priority) pairs for A*. */
class MinHeap {
  private idx: number[] = [];
  private pri: number[] = [];

  get size(): number {
    return this.idx.length;
  }

  push(i: number, p: number): void {
    this.idx.push(i);
    this.pri.push(p);
    let n = this.idx.length - 1;
    while (n > 0) {
      const parent = (n - 1) >> 1;
      if ((this.pri[parent] as number) <= p) break;
      this.swap(n, parent);
      n = parent;
    }
  }

  pop(): number {
    const top = this.idx[0] as number;
    const lastI = this.idx.pop() as number;
    const lastP = this.pri.pop() as number;
    if (this.idx.length > 0) {
      this.idx[0] = lastI;
      this.pri[0] = lastP;
      let n = 0;
      for (;;) {
        const l = n * 2 + 1;
        const r = l + 1;
        let m = n;
        if (l < this.idx.length && (this.pri[l] as number) < (this.pri[m] as number)) m = l;
        if (r < this.idx.length && (this.pri[r] as number) < (this.pri[m] as number)) m = r;
        if (m === n) break;
        this.swap(n, m);
        n = m;
      }
    }
    return top;
  }

  private swap(a: number, b: number): void {
    const ti = this.idx[a] as number;
    this.idx[a] = this.idx[b] as number;
    this.idx[b] = ti;
    const tp = this.pri[a] as number;
    this.pri[a] = this.pri[b] as number;
    this.pri[b] = tp;
  }
}
